// frontend/src/components/auth/ProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../services/apiClient';
import { isHubRouteAdminBypass } from '../../constants/roles';
import { tenantNameToSlug } from '../../utils/tenantSlug';
import { canAccessSectionKey } from '../../utils/sectionAccess';

const ProtectedRoute = ({ children, requiredPermissions = [], requiredRoles = [], requiredSectionKey = null }) => {
  const location = useLocation();
  const { authenticated, loading, user } = useAuth();
  const [verifyingAuth, setVerifyingAuth] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);


  // Verify token by making an API call
  useEffect(() => {
    const verifyToken = async () => {
      if (!authenticated) {
        setVerifyingAuth(false);
        return;
      }

      try {
        const isMockAuth = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true';
        if (isMockAuth) {
          // In mock mode, do not call the backend for token verification.
          const isAdmWesMock = !!user?.platform_roles?.includes('admin_wes')
            || user?.is_superuser === true
            || user?.is_platform_admin === true
            || String(user?.email || '').toLowerCase() === 'admin@1wes.com'
            || String(user?.email || '').toLowerCase() === 'viniciusaiires@hotmail.com';
          const isAdminMock = isAdmWesMock || user?.roles?.some(role =>
            role === 'admin' || role === 'superuser' || role === 'Admin' || role === 'Superuser'
          );

          let authorized;
          const hasSectionAccess = !requiredSectionKey
            || isAdmWesMock
            || canAccessSectionKey(user, requiredSectionKey);
          if (!hasSectionAccess) {
            authorized = false;
          } else if (isAdmWesMock || (isAdminMock && requiredRoles.length === 0)) {
            authorized = true;
          } else if (requiredRoles.length === 0 && requiredPermissions.length === 0) {
            authorized = true;
          } else {
            const hasRequiredRole = requiredRoles.length > 0 &&
              (user?.roles?.some(role => requiredRoles.includes(role)) ?? false);
            const hasRequiredPermissions = requiredPermissions.length > 0 &&
              requiredPermissions.every(permission =>
                user?.permissions?.includes(permission)
              );
            authorized = hasRequiredRole || hasRequiredPermissions;
          }

          setIsAuthorized(authorized);
          return;
        }

        // Busca dados frescos de /auth/me para usar nos checks de permissão.
        // Não depender do user em contexto (pode estar desatualizado se o backend
        // foi reiniciado ou se o select-tenant mudou o scope sem full reload).
        const meResponse = await apiClient.get('/auth/me');
        const freshUser = meResponse?.data || {};

        // Access rules:
        //   1. platform admin (is_superuser / platform_roles) bypassa tudo
        //   2. rota sem restrições só precisa de sessão autenticada
        //   3. senão, satisfaz EITHER role list OR permission list
        const isAdmWes = !!freshUser?.platform_roles?.includes('admin_wes')
          || freshUser?.is_superuser === true
          || freshUser?.is_platform_admin === true;

        const effectiveRoles = freshUser?.roles || user?.roles || [];
        const effectivePermissions = freshUser?.permissions || user?.permissions || [];
        const isAdmin = isAdmWes || isHubRouteAdminBypass(effectiveRoles);
        const hasSectionAccess = !requiredSectionKey
          || isAdmWes
          || canAccessSectionKey(freshUser, requiredSectionKey);

        let authorized;
        if (!hasSectionAccess) {
          authorized = false;
        } else if (isAdmWes || (isAdmin && requiredRoles.length === 0)) {
          authorized = true;
        } else if (requiredRoles.length === 0 && requiredPermissions.length === 0) {
          authorized = true;
        } else {
          const hasRequiredRole = requiredRoles.length > 0 &&
            (effectiveRoles.some(role => requiredRoles.includes(role)) ?? false);
          const hasRequiredPermissions = requiredPermissions.length > 0 &&
            requiredPermissions.every(permission =>
              effectivePermissions.includes(permission)
            );
          authorized = hasRequiredRole || hasRequiredPermissions;
        }

        setIsAuthorized(authorized);
      } catch (error) {
        // Token verification failed - user will be redirected to login
        setIsAuthorized(false);
      } finally {
        setVerifyingAuth(false);
      }
    };

    verifyToken();
  }, [authenticated, user, requiredPermissions, requiredRoles, requiredSectionKey]);

  // Show loading state while verifying auth
  if (loading || verifyingAuth) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // If user is not authenticated, redirect to login
  if (!authenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If user doesn't have required permissions/roles
  if (!isAuthorized && (requiredPermissions.length > 0 || requiredRoles.length > 0)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If we're at the root of a tenant path, redirect to the dashboard
  const tenantSlug = tenantNameToSlug(user?.tenant_name);
  if (user && tenantSlug && location.pathname === `/${tenantSlug}`) {
    return <Navigate to={`/${tenantSlug}/`} replace />;
  }

  return children;
};

export default ProtectedRoute;
